import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// VAPID public key - for web push
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if notifications are supported
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = async () => {
    if (!isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        await subscribeToPush();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const subscribeToPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Check for existing subscription
      let sub = await registration.pushManager.getSubscription();
      
      if (!sub) {
        // Create new subscription
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }

      setSubscription(sub);

      // Send subscription to server
      const userId = localStorage.getItem('userId');
      if (userId && sub) {
        await axios.post(`${BACKEND_URL}/api/notifications/subscribe`, {
          user_id: userId,
          subscription: sub.toJSON()
        }, { withCredentials: true });
      }

      return sub;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return null;
    }
  };

  const unsubscribe = async () => {
    try {
      if (subscription) {
        await subscription.unsubscribe();
        setSubscription(null);

        // Remove from server
        const userId = localStorage.getItem('userId');
        if (userId) {
          await axios.post(`${BACKEND_URL}/api/notifications/unsubscribe`, {
            user_id: userId
          }, { withCredentials: true });
        }
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
    }
  };

  // Show local notification (for in-app notifications)
  const showLocalNotification = (title, body, options = {}) => {
    if (permission === 'granted' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          body,
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-72x72.png',
          dir: 'rtl',
          lang: 'ar',
          vibrate: [100, 50, 100],
          ...options
        });
      });
    }
  };

  return {
    permission,
    subscription,
    isSupported,
    requestPermission,
    unsubscribe,
    showLocalNotification
  };
}

// Component for notification permission banner
export function NotificationBanner({ onClose }) {
  const { permission, isSupported, requestPermission } = useNotifications();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem('notificationBannerDismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  if (!isSupported || permission === 'granted' || permission === 'denied' || dismissed) {
    return null;
  }

  const handleEnable = async () => {
    const success = await requestPermission();
    if (success) {
      setDismissed(true);
      onClose && onClose();
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('notificationBannerDismissed', 'true');
    setDismissed(true);
    onClose && onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      right: '20px',
      maxWidth: '400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #00897B, #00B4A2)',
      borderRadius: '16px',
      padding: '16px 20px',
      boxShadow: '0 8px 32px rgba(0, 137, 123, 0.3)',
      zIndex: 1000,
      animation: 'slideUp 0.3s ease-out'
    }}>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '28px' }}>🔔</span>
        <div style={{ flex: 1 }}>
          <h4 style={{ color: '#fff', margin: '0 0 4px', fontSize: '15px', fontWeight: '600' }}>
            تفعيل الإشعارات
          </h4>
          <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '13px', lineHeight: '1.5' }}>
            فعّل الإشعارات ليصلك تنبيه فوري عند وصول طلب اتصال أو رسالة جديدة
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
        <button
          onClick={handleEnable}
          style={{
            flex: 1,
            padding: '10px 16px',
            background: '#fff',
            color: '#00897B',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          تفعيل
        </button>
        <button
          onClick={handleDismiss}
          style={{
            padding: '10px 16px',
            background: 'rgba(255,255,255,0.2)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          لاحقاً
        </button>
      </div>
    </div>
  );
}

export default NotificationBanner;
